import React, { useEffect, useState } from "react";
import { FaStar, FaShare } from "react-icons/fa";
import HeartIcon from "../Icons/HeartIcon";
import Lottie from "lottie-react";
import { useGlobalModals } from "../Common/GlobalModals";
import "./UnitCard.css";

const UnitCard = ({
  unit,
  language,
  onUnitSelect,
  onToggleFavorite,
  onReservation,
  onShare,
  isLoadingFavorite = false,
}) => {
  // The backend exposes the authoritative favorite flag as `is_favorited` on the
  // parcel root, while older nested payloads still carry `is_favorite`. Read both
  // so the heart reflects the real state in either shape.
  const isFavorited = unit?.is_favorited ?? unit?.is_favorite ?? false;
  // Use global modals
  const { openIframeModal, openPhotoGalleryModal, openVideoModal } = useGlobalModals();

  // Animation states
  const [tour360Animation, setTour360Animation] = useState(null);
  const [blueprint3DAnimation, setBlueprint3DAnimation] = useState(null);
  const [blueprint2DAnimation, setBlueprint2DAnimation] = useState(null);
  const [photoGalleryAnimation, setPhotoGalleryAnimation] = useState(null);
  const [videoAnimation, setVideoAnimation] = useState(null);

  // Load animations when component mounts
  useEffect(() => {
    const loadAnimations = async () => {
      try {
        const [tour360, blueprint3D, blueprint2D, photoGallery, video] =
          await Promise.all([
            import("../../assets/animations/360-view-white.json"),
            import("../../assets/animations/8-3D Modeling_opt.json"),
            import("../../assets/animations/17 Blueprint_opt.json"), // Using same for 2D
            import("../../assets/animations/09-Photo gallery_opt.json"),
            import("../../assets/animations/video-white-animation.json"),
          ]);

        setTour360Animation(tour360.default);
        setBlueprint3DAnimation(blueprint3D.default);
        setBlueprint2DAnimation(blueprint2D.default);
        setPhotoGalleryAnimation(photoGallery.default);
        setVideoAnimation(video.default);
      } catch (error) {}
    };

    loadAnimations();
  }, []);
  const formatPrice = (price) => {
    if (!price) return "-"; // language === 'ar' ? 'غير محدد' : 'Not specified';
    return new Intl.NumberFormat("en-US").format(
      price
    );
  };

  // The API returns numeric strings (sometimes with commas, e.g. "200,000")
  // alongside legacy numeric `price`. Coerce to a Number for math/formatting.
  const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return value;
    const parsed = parseFloat(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const totalPrice = toNumber(unit.price_with_tax ?? unit.price);
  const reservationAmount =
    toNumber(unit.reservation_amount) || Math.floor(totalPrice * 0.1);
  const downPayment =
    toNumber(unit.down_payment) || Math.floor(totalPrice * 0.2);
  const monthlyPayment = totalPrice
    ? Math.floor((totalPrice * 0.7) / 84)
    : 0;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "#22c55e"; // green
      case "reserved":
        return "#f59e0b"; // amber
      case "sold":
        return "#ef4444"; // red
      default:
        return "#6b7280"; // gray
    }
  };

  const getStatusText = (status) => {
    if (!status) return "-"; //language === "ar" ? "غير محدد" : "Unknown";

    const statusMap = {
      available: language === "ar" ? "متاح" : "Available",
      reserved: language === "ar" ? "محجوز" : "Reserved",
      sold: language === "ar" ? "مباع" : "Sold",
    };

    return statusMap[status.toLowerCase()] || status;
  };

  const handleUnitClick = () => {
    if (onUnitSelect) {
      onUnitSelect(unit);
    }
  };

  const handleReservationClick = (e) => {
    e.stopPropagation();
    if (onReservation) {
      onReservation(unit, unit.block?.project);
    }
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (isLoadingFavorite) return;
    if (onToggleFavorite) {
      onToggleFavorite(unit.id);
    }
  };

  const fallbackShare = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      alert(language === "ar" ? "تم نسخ الرابط إلى الحافظة" : "Link copied to clipboard");
    }).catch(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank');
    });
  };

  const handleShareClick = (e) => {
    e.stopPropagation();

    const baseUrl = window.location.origin + window.location.pathname;
    const projectId = unit?.project?.id || unit?.block?.project?.id;
    const unitId = unit?.id;

    if (projectId && unitId) {
      const shareUrl = `${baseUrl}?project=${projectId}&shared=true&unit_id=${unitId}`;
      const title = language === "ar"
        ? `وحدة ${unit?.parcel_number || unit?.unit_number || unitId}`
        : `Unit ${unit?.parcel_number || unit?.unit_number || unitId}`;

      if (navigator.share) {
        navigator.share({ title, text: "", url: shareUrl }).catch(() => {
          fallbackShare(shareUrl);
        });
      } else {
        fallbackShare(shareUrl);
      }
      return;
    }

    if (onShare) {
      onShare(unit);
    } else {
      console.error("Missing project ID or unit ID for sharing", unit);
    }
  };

  // New handler functions for additional features
  const handleVirtualTourClick = (e) => {
    e.stopPropagation();
    // Try unit virtual tour first, then fallback to project
    const virtualTourUrl = unit?.unit_virtual_tour_url || unit.block?.project?.virtual_tour_url;
    if (!virtualTourUrl) {
      return;
    }
    const isMobile = typeof navigator !== "undefined" &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      window.open(virtualTourUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const title =
      language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour";
    openIframeModal(virtualTourUrl, title, language);
  };

  const handleUnitVideoClick = (e) => {
    e.stopPropagation();
    // Try unit video first, then fallback to project
    const videoUrl = unit?.unit_video_url || unit.project?.promotional_video_url;
    if (!videoUrl) {
      // console.log("ddddd");
      return;
    }
    const title =
      language === "ar" ? "فيديو الوحدة" : "Unit Video";
    openVideoModal(videoUrl, title, language);
  };

  const handle3DBlueprintClick = (e) => {
    e.stopPropagation();
    if (!unit?.blueprint_3d_url) {
      return;
    }
    const blueprint3DUrl = unit.blueprint_3d_url;
    const title = language === "ar" ? "مخطط ثلاثي الأبعاد" : "3D Blueprint";
    const isImage = /\.(png|jpe?g|webp|gif)$/i.test(blueprint3DUrl);
    if (isImage) {
      openPhotoGalleryModal([blueprint3DUrl], title, language);
    } else {
      openIframeModal(blueprint3DUrl, title, language);
    }
  };

  const handle2DBlueprintClick = (e) => {
    e.stopPropagation();

    // Check if we have either URL or image
    const hasUrl = unit?.blueprint_2d_url;
    const hasImage = unit?.blueprint_2d_image_url;

    if (!hasUrl && !hasImage) {
      return;
    }

    const title = language === "ar" ? "مخطط ثنائي الأبعاد" : "2D Blueprint";

    // Prefer image over URL for better viewing experience
    if (hasImage) {
      // Use photo gallery modal for single image
      openPhotoGalleryModal([unit.blueprint_2d_image_url], title, language);
    } else if (hasUrl) {
      // Use iframe modal for URL
      openIframeModal(unit.blueprint_2d_url, title, language);
    }
  };

  const handlePhotoGalleryClick = (e) => {
    e.stopPropagation();
    // Try unit photos first, then fallback to project photos
    const photos = (unit?.unit_photo_gallery_image_urls && unit.unit_photo_gallery_image_urls.length > 0)
      ? unit.unit_photo_gallery_image_urls
      : unit?.photo_gallery_image_urls;
    if (!photos || photos.length === 0) {
      return;
    }
    const projectName =
      language === "ar"
        ? unit.block?.project?.name_ar || unit?.project?.name || "المشروع"
        : unit.block?.project?.name_en || unit?.project?.name || "Project";
    openPhotoGalleryModal(photos, projectName, language);
  };

  const handleContractDocumentClick = (e) => {
    e.stopPropagation();
    if (!unit?.contract_document_url && !unit?.contract_document) {
      return;
    }
    const contractUrl = unit.contract_document_url || unit.contract_document;
    window.open(contractUrl, "_blank");
  };

  return (
    <div className="unit-card-container" onClick={handleUnitClick}>
      <div className="card-container">
        {/* Left Side - Details */}
        <div className="card-left">

          {/* Price Header */}
          <div className="price-header" style={{ marginLeft:10,marginRight:10 }}>
            <div className="price-info">
              <span>{language === "ar" ? "احجز بـ" : "Reserve for"}</span>
              <span className="price-amount-bold">
                {formatPrice(reservationAmount)}
              </span>
              <span className="price-amount">
                {language === "ar" ? "ريال" : "SAR"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            
                  <div
                className="availability-badge project-name-badge"
                style={{
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  marginInline: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 140,
                }}
              >
                <span>
                  {language === "ar"
                    ? unit.project?.name_ar ||
                      unit?.project?.name ||
                      "المشروع"
                    : unit?.project?.name_en ||
                      unit?.project?.name ||
                      "Project"}
                </span>
              </div>
              <div
                className="availability-badge"
                style={{
                  backgroundColor: getStatusColor(
                    unit.sales_status || "available"
                  ),
                  marginLeft: 4,
                  marginInline: 0,
                }}
              >
                <span>
                  {getStatusText(unit.sales_status || "available")} -{" "}
                  {unit.parcel_number}
                </span>
              </div>
        
            </div>
          </div>

          <hr className="hr-divider"  style={{ marginLeft:10,marginRight:10 }} />

          {/* Down Payment Section */}
          <div className="price-section"  style={{ marginLeft:10,marginRight:10 }}>
            <span className="text-bold">
              {language === "ar" ? "دفعة أولى" : "Down Payment"}
            </span>
            <span className="price-amount-bold">
              {formatPrice(downPayment)}
            </span>
            <span className="price-amount-margin">
              {language === "ar" ? "ريال" : "SAR"}
            </span>
          </div>

          <hr className="hr-divider"  style={{ marginLeft:10,marginRight:10 }} />

          {/* Monthly Payment Section */}
          <div className="monthly-section"  style={{ marginLeft:10,marginRight:10 }} >
            <span>{language === "ar" ? "قسط شهري" : "Monthly Payment"}</span>
            <span className="price-amount-bold">
              {formatPrice(monthlyPayment)}
            </span>
            <span>{language === "ar" ? "ريال" : "SAR"}</span>
            <span className="monthly-installment">-</span>
            <span>{language === "ar" ? "لمدة 7 سنوات" : "for 7 years"}</span>
          </div>

          <hr className="hr-divider"  style={{ marginLeft:10,marginRight:10 }} />

          {/* Total Price Section */}
          <div className="total-price-section text-small"  style={{ marginLeft:10,marginRight:10 }}>
            <span>{language === "ar" ? "السعر الإجمالي" : "Total Price"}</span>
            <span className="price-amount-bold">{formatPrice(totalPrice)}</span>
            <span>{language === "ar" ? "ريال" : "SAR"}</span>
          </div>

          {/* Features Section */}
         
           {unit.unit_type === "residential" && (
          <div className="features-section" dir="ltr"  style={{ marginLeft:10,marginRight:10 ,visibility:unit.unit_type === "residential"?"visible":"hidden" }}>
      

            <div className="feature-item border-not-first">
              <div className="feature-icon-placeholder">
                <img
                  src="/unit_items/measurement.svg"
                  alt="Area"
                  className="feature-icon"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                />
              </div>
              <p className="feature-text">{(unit.area ? String(unit.area).replaceAll(".00", " ") : "120")}m²</p>
            </div>

                {/* Show floor only if unit is residential */}
          {unit.unit_type === "residential" && (
            <div className="feature-item border-not-first">
              <div className="feature-icon-placeholder">
              <p className="feature-text" style={{ fontWeight:'bold', fontSize:13 }}>
                {language === "ar"?
                  "الطابق"
                : 
                  "Floor"}
              </p>


                {/* <img
                  src="/unit_items/floor.svg"
                  alt="Floor"
                  className="feature-icon"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                /> */}
              </div>
              <p className="feature-text">{unit.floor_number || "-"}</p>
            </div>
          )}
              
            <div className="feature-item border-not-first">
              <div className="feature-icon-placeholder">
                <img
                  src="/unit_items/bed.svg"
                  alt="Bedrooms"
                  className="feature-icon"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                />
              </div>
              <p className="feature-text">{unit.bedrooms || "1"}</p>
            </div>
            <div className="feature-item border-not-first">
              <div className="feature-icon-placeholder">
                <img
                  src="/unit_items/bath.svg"
                  alt="Bathrooms"
                  className="feature-icon"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                />
              </div>
              <p className="feature-text">{unit.bathrooms || "1"}</p>
            </div>
            <div className="feature-item border-not-first">
              <div className="feature-icon-placeholder">
                <img
                  src="/unit_items/daisy-alt.svg"
                  alt="Garden"
                  className="feature-icon"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                />
              </div>
              <p className="feature-text">{unit.garden ? "✓" : "-"}</p>
            </div>
            <div className="feature-item border-not-first">
              <div className="feature-icon-placeholder">
                <img
                  src="/unit_items/N.png"
                  alt="Direction"
                  className="feature-icon"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                />
              </div>
              <p className="feature-text">{unit.direction || "-"}</p>
            </div>
          </div>
           )}
          {/* Reserve Button */}
          <button className="reserve-button" onClick={handleReservationClick}>
            {language === "ar" ? "حجز الوحدة" : "Reserve Unit"}
          </button>
        </div>

        {/* Right Side - Image and Info */}
        <div
          className="card-right"
          style={{
            backgroundImage: `url(${
              unit.image_url ||
              unit.project_image_desktop_url ||
              "/images/default-unit.jpg"
            })`,
          }}
        >
          {/* Top Section */}
          <div className="top-section" dir="ltr">
            {/* Logo Container */}
            <div className="logo-container">
              {unit.developer_logo_url && (
                <img
                  src={unit.developer_logo_url}
                  alt="Developer Logo"
                  className="developer-logo"
                />
              )}
              <div className="rating-badge" dir="ltr">
                <svg
                  className="star-icon"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ filter: "grayscale(100%) brightness(0.5)" }}
                >
                  <path
                    d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                    fill="currentColor"
                  />
                </svg>
                <span>{unit.rating || "4.5"}</span>
              </div>
            </div>

            {/* Share Button */}
            <div className="share-button" onClick={handleShareClick}>
              <img
                src="/share white v2.png"
                className="share-icon"
                alt="share"
              />
            </div>
          </div>

          {/* Location Info  project.city.name_ar : project.city.name_en */}
          <div className="location-info">
            <p className="location-info-title">
              {language === "ar"
                ? unit.project?.name_ar ||
                  unit?.project?.name ||
                  "المشروع"
                : unit?.project?.name_en ||
                  unit?.project?.name ||
                  "Project"}
            </p>

            <p className="location-address">
              {/* //format date to dd/mm/yyy  location */}
              {(unit.project?.location || "") + ""}
            </p>
            <p className="location-date">
                <span style={{ marginRight:2,marginRight:2 }}>
                {language === 'ar' ?
                  (unit.project?.status === 'active' ? 'نشط' :
                   unit.project?.status === 'completed' ? 'مكتمل' :
                   unit.project?.status === 'inactive' ? 'غير نشط' :
                   unit.project?.status === 'pending_approval' ? 'قيد الموافقة' :
                   unit.project?.status === 'pre_launch' ? 'ما قبل البيع' :
                   unit.project?.status === 'off_plan' ? 'قيد الإنشاء' :
                   unit.project?.status === 'ready' ? 'جاهز للتسليم' :
                   unit.project?.status === 'unavailable' ? 'غير متاح' : unit.project?.status || '') :
                  (unit.project?.status === 'pending_approval' ? 'Pending Approval' :
                   unit.project?.status === 'pre_launch' ? 'Pre-launch' :
                   unit.project?.status === 'off_plan' ? 'Off-plan' :
                   unit.project?.status === 'ready' ? 'Ready' :
                   unit.project?.status === 'unavailable' ? 'Unavailable' :
                   unit.project?.status === 'completed' ? 'Completed' : unit.project?.status || '')
                }
              </span>

              {/* //format date to dd/mm/yyy */}
              {new Date(
                unit.completion_date ||
                  unit.project?.completion_date ||
                  "2027-01-01"
              ).toLocaleDateString("en-GB", {
                year: "numeric",
              })}

              {/* {unit.completion_date || unit.block?.project?.completion_date || "2027-01-01"} */}
            </p>
          </div>

          {/* Amenities Container - buttons */}
          <div className="amenities-container">
            <button
              className="share-icon amenities-share-btn"
              onClick={handleShareClick}
              title={language === "ar" ? "مشاركة" : "Share"}
              aria-label={language === "ar" ? "مشاركة" : "Share"}
            >
              <img
                src="/share white v2.png"
                alt="share"
              />
            </button>

            <button
              className="favorite-btn"
              onClick={handleFavoriteClick}
              disabled={isLoadingFavorite}
              aria-busy={isLoadingFavorite}
              title={
                isLoadingFavorite
                  ? language === "ar"
                    ? "جارٍ الحفظ..."
                    : "Saving..."
                  : isFavorited
                  ? language === "ar"
                    ? "إزالة من المفضلة"
                    : "Remove from Favorites"
                  : language === "ar"
                  ? "إضافة إلى المفضلة"
                  : "Add to Favorites"
              }
            >
              {isLoadingFavorite ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: "unitcard-fav-spin 0.8s linear infinite" }}
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <HeartIcon
                  filled={isFavorited}
                  size={20}
                  color={isFavorited ? "#dc2626" : "white"}
                />
              )}
            </button>

            {(unit?.virtual_tour_url || unit.project?.virtual_tour_url) && (
              <button
                className="share-icon"
                onClick={handleVirtualTourClick}
                title={
                  unit?.virtual_tour_url || unit.project?.virtual_tour_url
                    ? language === "ar"
                      ? "جولة افتراضية 360°"
                      : "360° Virtual Tour"
                    : language === "ar"
                    ? "لا توجد جولة افتراضية متاحة"
                    : "No virtual tour available"
                }
              >
                <Lottie
                  animationData={tour360Animation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {(unit?.video_url || unit.project?.promotional_video_url) && videoAnimation && (
              <button
                className="share-icon"
                onClick={handleUnitVideoClick}
                title={
                  unit?.video_url || unit.project?.promotional_video_url
                    ? language === "ar"
                      ? "فيديو الوحدة"
                      : "Unit Video"
                    : language === "ar"
                    ? "لا يوجد فيديو متاح"
                    : "No video available"
                }
              >
                <Lottie
                  animationData={videoAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {unit?.blueprint_3d_url && blueprint3DAnimation && (
              <button
                className="share-icon"
                onClick={handle3DBlueprintClick}
                title={
                  unit?.blueprint_3d_url
                    ? language === "ar"
                      ? "مخطط ثلاثي الأبعاد"
                      : "3D Blueprint"
                    : language === "ar"
                    ? "لا يوجد مخطط ثلاثي الأبعاد متاح"
                    : "No 3D blueprint available"
                }
              >
                <Lottie
                  animationData={blueprint3DAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {(unit?.blueprint_2d_url || unit?.blueprint_2d_image_url) && blueprint2DAnimation && (
              <button
                className="share-icon"
                onClick={handle2DBlueprintClick}
                title={
                  unit?.blueprint_2d_url || unit?.blueprint_2d_image_url
                    ? language === "ar"
                      ? "مخطط ثنائي الأبعاد"
                      : "2D Blueprint"
                    : language === "ar"
                    ? "لا يوجد مخطط ثنائي الأبعاد متاح"
                    : "No 2D blueprint available"
                }
              >
                <Lottie
                  animationData={blueprint2DAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {((unit?.unit_photo_gallery_image_urls && unit.unit_photo_gallery_image_urls.length > 0) ||
              (unit?.photo_gallery_image_urls && unit.photo_gallery_image_urls.length > 0)) && (
                <button
                  className="share-icon"
                  onClick={handlePhotoGalleryClick}
                  title={
                    unit.photo_gallery_image_urls &&
                    unit.photo_gallery_image_urls.length > 0
                      ? language === "ar"
                        ? "معرض الصور"
                        : "Photo Gallery"
                      : language === "ar"
                      ? "لا توجد صور متاحة"
                      : "No photos available"
                  }
                >
                  <Lottie
                    animationData={photoGalleryAnimation}
                    loop={true}
                    autoplay={true}
                    style={{ width: 30, height: 30 }}
                  />
                </button>
              )}

            {(unit?.contract_document_url || unit?.contract_document) && (
              <button
                className="share-icon"
                onClick={handleContractDocumentClick}
                title={
                  unit.contract_document_url || unit.contract_document
                    ? language === "ar"
                      ? "مستند العقد"
                      : "Contract Document"
                    : language === "ar"
                    ? "لا يوجد مستند عقد متاح"
                    : "No contract document available"
                }
              >
                <img
                  src="/icons/contract-white.png"
                  alt="Contract"
                  style={{ width: 20, height: 20 }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitCard;
